import { useEffect, useMemo, useState } from "react";

import { canRunPrecheck } from "utils/parametric";
import { startCommunityEcmValidation } from "utils/communities/api";
import {
    getCommunityValidationComboCount,
    COMMUNITY_VALIDATION_COMBO_MIN,
    COMMUNITY_VALIDATION_COMBO_MAX,
} from "utils/communities/validationComboCount";
import { getTableUpgradesMissingFromEcmInputMap } from "utils/upgrades/ecmInputMap";
import { useCommunityValidationLock } from "../CommunityValidationLockContext";
import commParametricRunConfig from "../templates/commParametricRunConfig.json";

export const useCommunityValidationControls = ({
    communityId,
    uid,
    user,
    models = {},
    ecmInputMap = {},
    upgradePackages = [],
    persistCommunityForSimulation,
    /** Archetype chosen in the upgrades table base-case column */
    baseModelId = null,
}) => {
    const {
        validationUiLocked,
        validationTimedOut,
        serverSaysValidationPending,
        lastValidationRun = {},
    } = useCommunityValidationLock();

    const [disableValidateClick, setDisableValidateClick] = useState(false);
    const [validationStarting, setValidationStarting] = useState(false);

    const validationModelId = baseModelId || "";
    const validationInProgress = validationUiLocked;

    useEffect(() => {
        if (serverSaysValidationPending) {
            setValidationStarting(false);
        }
    }, [serverSaysValidationPending]);

    const upgradePackagesMap = useMemo(
        () => upgradePackages.reduce((acc, pkg) => ({ ...acc, [pkg.id]: pkg }), {}),
        [upgradePackages]
    );

    const ecmTableSyncIssues = useMemo(
        () => getTableUpgradesMissingFromEcmInputMap(upgradePackagesMap, ecmInputMap),
        [upgradePackagesMap, ecmInputMap]
    );

    const ecmTableSyncIssueLabels = useMemo(
        () =>
            [...new Set(
                ecmTableSyncIssues.map(
                    ({ packageName, labelText, category }) =>
                        `${packageName}: ${labelText}${category ? ` (${category})` : ""}`
                )
            )],
        [ecmTableSyncIssues]
    );

    const validationComboCount = useMemo(() => getCommunityValidationComboCount(ecmInputMap), [ecmInputMap]);
    const validationComboOutOfRange =
        validationComboCount < COMMUNITY_VALIDATION_COMBO_MIN || validationComboCount > COMMUNITY_VALIDATION_COMBO_MAX;

    const baseModelReady = Boolean(validationModelId && models[validationModelId]?.status === "ready");
    const baseModelName = validationModelId ? models[validationModelId]?.name : "";

    const validationRefModelName =
        lastValidationRun?.modelId && models[lastValidationRun.modelId]?.name
            ? models[lastValidationRun.modelId].name
            : "";

    let validationModelCaption = "";
    if (validationStarting && validationModelId) {
        validationModelCaption = baseModelName
            ? `Starting validation (${baseModelName})`
            : "Starting validation";
    } else if (validationRefModelName && lastValidationRun?.id) {
        if (validationInProgress) {
            validationModelCaption = `Validation in progress using: ${validationRefModelName}`;
        } else if (lastValidationRun.complete) {
            validationModelCaption = `Last validation run used: ${validationRefModelName}`;
        } else if (validationTimedOut && serverSaysValidationPending) {
            validationModelCaption = `Validation did not complete within 2 minutes for: ${validationRefModelName}. You can try again.`;
        }
    }

    const validationModelDiffersFromLastRun =
        Boolean(validationModelId) &&
        Boolean(lastValidationRun?.modelId) &&
        validationModelId !== lastValidationRun.modelId;

    const validateButtonTooltip = useMemo(() => {
        if (validationUiLocked || validationStarting) {
            return "Validation is already in progress.";
        }
        if (!validationModelId) {
            return "Choose a base case model in the table below (Base column header).";
        }
        if (!baseModelReady) {
            return "The selected base case model must be in ready status.";
        }
        if (Object.values(ecmInputMap).every((upgradeToCheck) => Object.keys(upgradeToCheck).length === 1)) {
            return "Add active upgrade measures before validating.";
        }
        if (!canRunPrecheck(ecmInputMap) && !validationModelDiffersFromLastRun) {
            return "Validation already matches the current set of upgrades for this base case — change measures or pick a different base case.";
        }
        if (validationComboOutOfRange) {
            return "Validation batch size is out of range — adjust active measures.";
        }
        if (ecmTableSyncIssues.length > 0) {
            return `Fix upgrades that did not save correctly:\n${ecmTableSyncIssueLabels
                .map((label) => `• ${label}`)
                .join("\n")}`;
        }
        return baseModelName ? `Validate using base case: ${baseModelName}` : "";
    }, [
        validationUiLocked,
        validationStarting,
        validationModelId,
        baseModelReady,
        baseModelName,
        ecmInputMap,
        validationModelDiffersFromLastRun,
        validationComboOutOfRange,
        ecmTableSyncIssues,
        ecmTableSyncIssueLabels,
    ]);

    const disableValidateButton =
        disableValidateClick ||
        !baseModelReady ||
        Object.values(ecmInputMap).every((upgradeToCheck) => Object.keys(upgradeToCheck).length === 1) ||
        (!canRunPrecheck(ecmInputMap) && !validationModelDiffersFromLastRun) ||
        validationUiLocked ||
        validationStarting ||
        validationComboOutOfRange ||
        ecmTableSyncIssues.length > 0;

    const handleCommunityValidateClick = async () => {
        setValidationStarting(true);
        setDisableValidateClick(true);
        try {
            const savedMap = await persistCommunityForSimulation();
            if (!communityId || !validationModelId) {
                setValidationStarting(false);
                return;
            }

            const { uid: userUid = "", email: userEmail = "" } = user || {};
            const mapToSend = savedMap || ecmInputMap;

            const validationResult = await startCommunityEcmValidation({
                communityId,
                modelId: validationModelId,
                parametricRunConfig: {
                    ...commParametricRunConfig,
                    user: {
                        uid: userUid || uid,
                        email: userEmail,
                    },
                    ecmInputMap: mapToSend,
                },
            });
            if (!validationResult.ok) {
                setValidationStarting(false);
                window.alert(
                    validationResult.message ||
                        `Validation could not be started${validationResult.status ? ` (HTTP ${validationResult.status})` : ""}.`
                );
            }
        } catch {
            setValidationStarting(false);
        } finally {
            setDisableValidateClick(false);
        }
    };

    return {
        validationModelCaption,
        validationInProgress,
        validationStarting,
        validationUiLocked,
        validationComboOutOfRange,
        ecmTableSyncIssues,
        disableValidateButton,
        validateButtonTooltip,
        handleCommunityValidateClick,
        baseModelName,
    };
};
